import type { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';

import type { RegisterAnalytics } from '~features/annals/types';
import { handlePromocodeFromQuery } from '~features/promocode/utils/handlePromocodeFromQuery';
import { loadFromSessionStorage, ssKeys } from '~features/storage';
import { MarkersService } from '~services/markers';
import { api, apiTags } from '~shared/api/origin';
import { TokenManager } from '~shared/services/ClientTokenManager';
import { getLanguageAgnosticHref } from '~shared/utils/getLanguageAgnosticHref';
import { isSafeApp } from '~shared/utils/host';

import type {
	TAuthDemoUserInfo,
	TAuthLoginCreds,
	TAuthUserInfo,
} from './types';

const prefix = '/identity';

export const authApi = api.injectEndpoints({
	endpoints: (build) => ({
		// Authentication state is stored in the authSlice
		authenticate: build.query<TAuthUserInfo, undefined>({
			async queryFn(_, _queryApi, _extraOptions, fetchWithBQ) {
				const { token, type } = TokenManager.getInfo();

				handlePromocodeFromQuery(new URLSearchParams(window.location.search));
				const sent = loadFromSessionStorage(
					ssKeys.ANNALS_SITE_VISIT_SENT,
					false
				);
				if (!sent) {
					MarkersService.init();
				}

				const createDemoSession = async () => {
					const result = (await _queryApi.dispatch(
						authApi.endpoints.registerDemoUser.initiate(undefined)
					)) as { data: TAuthUserInfo } | { error: FetchBaseQueryError };

					return result;
				};
				// no token, create demo user and return info
				if (!token) {
					return createDemoSession();
				}
				// this token will be returned in baseQuery
				//try to use it for /identity
				try {
					const { data } = await fetchWithBQ(prefix);

					if (data) {
						return { data: data as TAuthUserInfo };
					}
					return createDemoSession();
				} catch {
					if (type === 'query') {
						TokenManager.clearToken('query');
					}
					return createDemoSession();
				}
			},
		}),
		registerDemoUser: build.mutation<TAuthUserInfo, undefined>({
			async queryFn(_, _queryApi, _extraOptions, fetchWithBQ) {
				try {
					const { data, error } = await fetchWithBQ({
						url: '/ai/demo/users',
						method: 'POST',
						body: { isSafe: isSafeApp, isJoiAi: isSafeApp },
					});
					if (error) {
						return {
							error,
						};
					}
					const demoData = data as TAuthDemoUserInfo;
					TokenManager.saveDemoCredentials(demoData);
					return {
						data: {
							id: demoData.userId,
							email: '',
							roles: ['demo'],
							isSafe: isSafeApp,
							isJoiAi: isSafeApp,
						} as TAuthUserInfo,
					};
				} catch (e) {
					return {
						error: {
							status: 'CUSTOM_ERROR',
							data: e,
						} as FetchBaseQueryError,
					};
				}
			},
		}),
		register: build.mutation<
			TAuthUserInfo,
			TAuthLoginCreds & { analytics: RegisterAnalytics }
		>({
			query: ({ email, password }) => ({
				url: `${prefix}`,
				method: 'PATCH',
				body: {
					email,
					password,
				},
			}),
			async onQueryStarted({}, { queryFulfilled }) {
				try {
					await queryFulfilled;
					TokenManager.makeDemoCredentialsMain();
				} catch {}
			},
			invalidatesTags: (result, error) =>
				error ? [] : [apiTags.tags, apiTags.contacts],
		}),
		registerDiscord: build.mutation<
			TAuthUserInfo,
			{ id: string; token: string } & { analytics: RegisterAnalytics }
		>({
			query: ({ id, token }) => ({
				url: `/ai/demo/users/discord/${id}`,
				method: 'PATCH',
				headers: {
					'x-redirect-url': getLanguageAgnosticHref(),
				},
				body: {
					token,
				},
			}),
			async onQueryStarted({}, { queryFulfilled }) {
				try {
					await queryFulfilled;
					TokenManager.makeDemoCredentialsMain();
				} catch {}
			},
			invalidatesTags: (result, error) =>
				error ? [] : [apiTags.tags, apiTags.contacts],
		}),
		discordLogin: build.mutation<TAuthUserInfo, { idToken: string }>({
			query: ({ idToken }) => ({
				url: prefix,
				method: 'GET',
				headers: {
					'x-redirect-url': getLanguageAgnosticHref(),
					authorization: `Discord token="${idToken}"`,
				},
			}),
		}),
		registerGoogle: build.mutation<
			TAuthUserInfo,
			{ id: string; token: string } & { analytics: RegisterAnalytics }
		>({
			query: ({ id, token }) => ({
				url: `/ai/demo/users/google/${id}`,
				method: 'PATCH',
				body: {
					token,
				},
			}),
			async onQueryStarted({}, { queryFulfilled }) {
				try {
					await queryFulfilled;
					TokenManager.makeDemoCredentialsMain();
				} catch {}
			},
			invalidatesTags: (result, error) =>
				error ? [] : [apiTags.tags, apiTags.contacts],
		}),
		googleLogin: build.mutation<TAuthUserInfo, { idToken: string }>({
			query: ({ idToken }) => ({
				url: prefix,
				method: 'GET',
				headers: {
					authorization: `Google token="${idToken}"`,
				},
			}),
		}),
		login: build.mutation<TAuthUserInfo, TAuthLoginCreds>({
			query: ({ email, password }) => ({
				url: `${prefix}`,
				method: 'GET',
				headers: {
					authorization: `Basic ${btoa(`${email}:${password}`)}`,
				},
			}),
		}),
		updateSafeMode: build.mutation<
			unknown,
			{ userId: string; isSafeMode: boolean }
		>({
			query: ({ userId, isSafeMode }) => ({
				url: `/users/safe/${userId}`,
				method: 'PUT',
				body: { isSafe: isSafeMode },
			}),
			invalidatesTags: [apiTags.contacts],
		}),
		changeEmail: build.mutation<unknown, { email: string }>({
			query: ({ email }) => ({
				url: `${prefix}`,
				method: 'PATCH',
				body: { email },
			}),
		}),
		sendResetPassword: build.mutation<unknown, { email: string }>({
			query: ({ email }) => ({
				url: 'reminders/password',
				method: 'POST',
				body: { type: 'password', email },
			}),
		}),
		setNewPassword: build.mutation<
			unknown,
			{ password: string; token: string }
		>({
			query: ({ password, token }) => ({
				url: prefix,
				method: 'PATCH',
				headers: {
					authorization: `Token token="${token}"`,
				},
				body: { password },
			}),
		}),
	}),
	overrideExisting: false,
});

export const {
	useAuthenticateQuery,
	useChangeEmailMutation,
	useUpdateSafeModeMutation,
	useSendResetPasswordMutation,
	useLoginMutation,
	useGoogleLoginMutation,
	useRegisterMutation,
	useRegisterGoogleMutation,
	useSetNewPasswordMutation,
} = authApi;
